// Utility layer (TS-2.5.04) - pure, no side effects. ProjectQuery (ProjectsController.cs)
// carries no sort field, so "sortable" is a client-side ordering of the CURRENT page only; a
// second page fetched afterward arrives in the server's own order and the column's arrow is the
// one honest description of what happened (this is recorded as decisions.md D-229).
import type { ProjectSummary } from "../../../api/generated/contracts";

export type ProjectSortColumn = "name" | "team" | "status" | "percentComplete" | "updatedAt";
export type SortDirection = "ascending" | "descending";

function compareStrings(left: string, right: string): number {
  return left.localeCompare(right);
}

/**
 * Nulls sort last regardless of direction - a "%- complete unknown" row staying at the bottom
 * either way is the more useful reading than it flipping to the top on descending, so the null
 * placement is decided here and the caller's ascending/descending `sign` is applied only to the
 * two-real-numbers case, never to a comparison involving a null.
 */
function compareNullableNumbers(left: number | null, right: number | null, sign: 1 | -1): number {
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return sign * (left - right);
}

function keyFor(column: ProjectSortColumn, row: ProjectSummary): string {
  switch (column) {
    case "name":
      return row.name;
    case "team":
      return row.rawTeam ?? "";
    case "status":
      return row.status.canonical;
    case "percentComplete":
      return "";
    case "updatedAt":
      return row.updatedAtUtc ?? "";
  }
}

export function sortProjects(
  rows: readonly ProjectSummary[],
  column: ProjectSortColumn,
  direction: SortDirection,
): ProjectSummary[] {
  const sign = direction === "ascending" ? 1 : -1;
  const sorted = [...rows];

  sorted.sort((left, right) => {
    if (column === "percentComplete") {
      return compareNullableNumbers(left.percentComplete, right.percentComplete, sign);
    }
    return sign * compareStrings(keyFor(column, left), keyFor(column, right));
  });

  return sorted;
}
